import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { LogWorkoutSetRequestDto, WeightUnit } from '@/api/types';
import { api } from '@/api/client';
import { useLogWorkout, useUpdateWorkout } from '@/api/hooks/useWorkout';
import { useToast } from '@/components/toast/ToastProvider';
import {
  DEFAULT_REST_SEC,
  deriveFinishKind,
  liveSessionReducer,
  type LiveSession,
  type SessionExercise,
  type SessionKind,
  type SessionSet,
} from './liveSessionReducer';

export type { LiveSession, SessionExercise, SessionKind, SessionSet };
export { DEFAULT_REST_SEC };

export const STORAGE_KEY = 'optimize.liveSession.v1';

function readStorage(): LiveSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as LiveSession;
  } catch {
    return null;
  }
}

interface AddExerciseInput {
  exerciseId: string | null;
  exerciseName: string;
  bodyweight: boolean;
  muscleGroups: string[];
}

export interface LiveSessionApi {
  active: LiveSession | null;
  hydrated: boolean;
  start: (kind: SessionKind, opts: { weightUnit: WeightUnit; cardioActivity?: string }) => void;
  addExercise: (input: AddExerciseInput) => void;
  removeExercise: (uid: string) => void;
  setExerciseNotes: (uid: string, notes: string) => void;
  logSet: (uid: string, input: { reps: number; weight: number | null; rpe: number | null; restSec?: number }) => Promise<void>;
  editSet: (uid: string, setIdx: number, input: { reps: number; weight: number | null; rpe: number | null }) => Promise<void>;
  removeSet: (uid: string, setIdx: number) => Promise<void>;
  cardioToggle: () => void;
  cardioSetDistance: (distance: number | null) => void;
  setNotes: (notes: string) => void;
  skipRest: () => void;
  extendRest: (sec: number) => void;
  finish: () => Promise<void>;
  cancel: () => void;
  totalCardioElapsedSec: () => number;
}

const Ctx = createContext<LiveSessionApi | null>(null);

export function useLiveSession(): LiveSessionApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useLiveSession must be used inside LiveSessionProvider');
  return v;
}

export function LiveSessionProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(liveSessionReducer, { active: null });
  const [hydrated, setHydrated] = useState(false);
  const hydratedRef = useRef(false);
  const logWorkout = useLogWorkout();
  const updateWorkout = useUpdateWorkout();
  const toast = useToast();
  // Latest state for the async closures below — avoids stale-state bugs when
  // logSet/finish are invoked, awaited, and then re-read state.
  const stateRef = useRef(state);
  stateRef.current = state;

  // Hydrate from localStorage on mount.
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    dispatch({ type: 'hydrate', session: readStorage() });
    setHydrated(true);
  }, []);

  // Persist on every change.
  useEffect(() => {
    if (!hydratedRef.current) return;
    if (state.active) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state.active)); } catch { /* quota — ignore */ }
    } else {
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    }
  }, [state.active]);

  const totalCardioElapsedSec = useCallback(() => {
    const s = stateRef.current.active;
    if (!s || s.kind !== 'cardio') return 0;
    if (!s.cardioRunningSince) return s.cardioElapsedSec;
    const running = (Date.now() - new Date(s.cardioRunningSince).getTime()) / 1000;
    return s.cardioElapsedSec + Math.max(0, running);
  }, []);

  // Lazily creates the parent WorkoutEntry so per-set rows can attach to it.
  const ensureWorkoutId = useCallback(async (kind: SessionKind, cardioActivity: string | null): Promise<string> => {
    const existing = stateRef.current.active?.workoutId;
    if (existing) return existing;
    const kindStr = kind === 'cardio' ? 'cardio' : 'lift';
    const res = await logWorkout.mutateAsync({
      kind: kindStr,
      cardioActivity: kind === 'cardio' ? cardioActivity : null,
      durationMinutes: 0,
      notes: null,
      loggedAt: new Date().toISOString(),
    });
    dispatch({ type: 'attachWorkoutId', workoutId: res.id });
    return res.id;
  }, [logWorkout]);

  const start: LiveSessionApi['start'] = useCallback((kind, opts) => {
    dispatch({ type: 'start', kind, weightUnit: opts.weightUnit, cardioActivity: opts.cardioActivity });
  }, []);

  const addExercise: LiveSessionApi['addExercise'] = useCallback((input) => {
    const uid = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    dispatch({
      type: 'addExercise',
      exercise: {
        uid,
        exerciseId: input.exerciseId,
        exerciseName: input.exerciseName,
        bodyweight: input.bodyweight,
        muscleGroups: input.muscleGroups,
        sets: [],
        notes: '',
      },
    });
  }, []);

  const logSet: LiveSessionApi['logSet'] = useCallback(async (uid, input) => {
    const s = stateRef.current.active;
    if (!s) return;
    const exercise = s.exercises.find((e) => e.uid === uid);
    if (!exercise) return;
    const setIdx = exercise.sets.length;
    const exerciseIndex = s.exercises.findIndex((e) => e.uid === uid);
    const loggedAt = new Date().toISOString();
    const restSec = input.restSec ?? s.restDefaultSec;

    const set: SessionSet = {
      reps: input.reps,
      weight: input.weight,
      rpe: input.rpe,
      loggedAt,
      saved: false,
    };
    dispatch({ type: 'logSet', uid, set, restSec });

    try {
      const workoutId = await ensureWorkoutId('lift', null);
      const body: LogWorkoutSetRequestDto = {
        workoutId,
        exerciseIndex,
        setIndex: setIdx,
        exerciseId: exercise.exerciseId,
        exerciseName: exercise.exerciseName,
        reps: input.reps,
        weight: input.weight,
        weightUnit: s.weightUnit,
        rpe: input.rpe,
        notes: null,
        loggedAt,
      };
      await api.post<void>('/api/workout/set', body);
      dispatch({ type: 'markSetSaved', uid, setIdx });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not save set';
      toast.show(`Set saved locally — sync failed: ${msg}`);
    }
  }, [ensureWorkoutId, toast]);

  const editSet: LiveSessionApi['editSet'] = useCallback(async (uid, setIdx, input) => {
    const s = stateRef.current.active;
    if (!s) return;
    const exercise = s.exercises.find((e) => e.uid === uid);
    if (!exercise) return;
    const existing = exercise.sets[setIdx];
    if (!existing) return;
    const exerciseIndex = s.exercises.findIndex((e) => e.uid === uid);
    dispatch({ type: 'editSet', uid, setIdx, reps: input.reps, weight: input.weight, rpe: input.rpe });

    try {
      // The set may never have synced (logSet failed) — ensure the parent
      // workout exists so the upsert below has something to attach to.
      const workoutId = await ensureWorkoutId('lift', null);
      const body: LogWorkoutSetRequestDto = {
        workoutId,
        exerciseIndex,
        setIndex: setIdx,
        exerciseId: exercise.exerciseId,
        exerciseName: exercise.exerciseName,
        reps: input.reps,
        weight: input.weight,
        weightUnit: s.weightUnit,
        rpe: input.rpe,
        notes: null,
        // Preserve original log time so re-saving an edit doesn't reorder sets.
        loggedAt: existing.loggedAt,
      };
      await api.post<void>('/api/workout/set', body);
      dispatch({ type: 'markSetSaved', uid, setIdx });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not update set';
      toast.show(`Set updated locally — sync failed: ${msg}`);
    }
  }, [ensureWorkoutId, toast]);

  const removeSet: LiveSessionApi['removeSet'] = useCallback(async (uid, setIdx) => {
    const s = stateRef.current.active;
    if (!s) return;
    const eIdx = s.exercises.findIndex((e) => e.uid === uid);
    dispatch({ type: 'removeSet', uid, setIdx });
    const workoutId = s.workoutId;
    if (workoutId && eIdx >= 0) {
      try {
        await api.delete<void>(`/api/workout/${encodeURIComponent(workoutId)}/set/${eIdx}/${setIdx}`);
      } catch { /* local state already updated */ }
    }
  }, []);

  const finish: LiveSessionApi['finish'] = useCallback(async () => {
    const s = stateRef.current.active;
    if (!s) return;

    let durationMin: number;
    if (s.kind === 'cardio') {
      durationMin = Math.max(1, Math.round(totalCardioElapsedSec() / 60));
    } else {
      const ms = Date.now() - new Date(s.startedAt).getTime();
      durationMin = Math.max(1, Math.round(ms / 60_000));
    }

    const kindStr = deriveFinishKind(s);

    try {
      if (s.workoutId) {
        await updateWorkout.mutateAsync({
          id: s.workoutId,
          body: {
            kind: kindStr,
            cardioActivity: s.kind === 'cardio' ? s.cardioActivity : null,
            durationMinutes: durationMin,
            notes: s.notes || null,
          },
        });
      } else {
        await logWorkout.mutateAsync({
          kind: kindStr,
          cardioActivity: s.kind === 'cardio' ? s.cardioActivity : null,
          durationMinutes: durationMin,
          notes: s.notes || null,
          loggedAt: s.startedAt,
        });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not save workout';
      toast.show(`Finish failed: ${msg}`);
      // Bail without clearing state so the user can retry from /train/session.
      return;
    }

    dispatch({ type: 'finish' });
  }, [logWorkout, updateWorkout, totalCardioElapsedSec, toast]);

  const value = useMemo<LiveSessionApi>(() => ({
    active: state.active,
    hydrated,
    start,
    addExercise,
    removeExercise: (uid) => dispatch({ type: 'removeExercise', uid }),
    setExerciseNotes: (uid, notes) => dispatch({ type: 'setExerciseNotes', uid, notes }),
    logSet,
    editSet,
    removeSet,
    cardioToggle: () => dispatch({ type: 'cardioToggle' }),
    cardioSetDistance: (d) => dispatch({ type: 'cardioSetDistance', distance: d }),
    setNotes: (n) => dispatch({ type: 'setNotes', notes: n }),
    skipRest: () => dispatch({ type: 'skipRest' }),
    extendRest: (sec) => dispatch({ type: 'extendRest', sec }),
    finish,
    cancel: () => dispatch({ type: 'finish' }),
    totalCardioElapsedSec,
  }), [state.active, hydrated, totalCardioElapsedSec, logSet, editSet, removeSet, finish, start, addExercise]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
